import React from "react";
import {
  CreateIleInput,
  IleObjectiveInput,
  useCreateStandaloneIleMutation,
  VideoDataInput,
} from "@axio/graphql/generated/graphql";
import InteractiveLearningFormDataContainer from "./InteractiveLearningFormDataContainer";

type InteractiveLearningLTIFormContainerProps = {
  onSuccess: (ileId: string, title: string, dueDate?: string | null) => void;
  onError: (error: Error) => void;
  onCancel?: () => void;
  initialValues?: Partial<InteractiveLearningFormPayload>;
  currentDomain?: string;
  imageGenerationSupportFlag?: boolean;
};

type InteractiveLearningObjectivePayload = {
  objective: string;
  sequence?: number;
  evalCriteria?: string[];
};

type InteractiveLearningVideoPayload = {
  videoId: string;
  objectiveText: string;
  objectiveSequence: number;
  isTeacherAdded?: boolean;
};

type InteractiveLearningFormPayload = {
  title: string;
  description?: string;
  context?: string;
  objectives: InteractiveLearningObjectivePayload[];
  videoPool?: InteractiveLearningVideoPayload[];
  ileType?: CreateIleInput["ileType"];
  dueDate?: string | null;
};

const getErrorFromUnknown = (error: unknown) => {
  if (error instanceof Error) {
    return error;
  }
  return new Error("Failed to create interactive learning experience");
};

const mapObjectives = (objectives: InteractiveLearningObjectivePayload[] = []): IleObjectiveInput[] =>
  objectives.map((objective, index) => ({
    objective: objective.objective,
    sequence: objective.sequence ?? index + 1,
    evalCriteria: objective.evalCriteria || [],
  }));

const mapVideoPool = (videoPool: InteractiveLearningVideoPayload[] = []): VideoDataInput[] =>
  videoPool.map((video) => ({
    videoId: video.videoId,
    objectiveText: video.objectiveText,
    objectiveSequence: video.objectiveSequence,
    isTeacherAdded: Boolean(video.isTeacherAdded),
  }));

const InteractiveLearningLTIFormContainer = ({
  onSuccess,
  onError,
  onCancel,
  initialValues,
  currentDomain,
  imageGenerationSupportFlag = false,
}: InteractiveLearningLTIFormContainerProps) => {
  const [createIleMutation, { loading: isSubmitting }] = useCreateStandaloneIleMutation();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleSave = async (activityDataInput: unknown) => {
    try {
      const activityData = activityDataInput as InteractiveLearningFormPayload;

      // Normalize dueDate: the DatePicker produces a Date at midnight *local* time.
      // Calling toISOString() converts to UTC, rolling the date back by the user's
      // UTC offset (e.g. midnight IST → 18:30 UTC the previous day, showing up as
      // the wrong date in D2L). Instead, read the local calendar fields and pin to
      // UTC noon. This is safe for UTC-12 through UTC+12 and the vast majority of
      // UTC+13/+14 locales; the only edge case is UTC+14 (Kiribati Line Islands)
      // where noon UTC appears as 02:00 the next calendar day.
      const normalizeDueDate = (raw: string | Date | null | undefined): string | null => {
        if (!raw) return null;
        const d = raw instanceof Date ? raw : new Date(raw);
        if (isNaN(d.getTime())) return null;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}T12:00:00.000Z`;
      };

      const normalizedDueDate = normalizeDueDate(activityData.dueDate);

      // dueDate is not yet in the generated CreateIleInput type; it will be added
      // once pnpm graphql:generate is re-run against the updated schema.
      const ileInput = {
        title: activityData.title,
        description: activityData.description || "",
        context: activityData.context || "",
        objectives: mapObjectives(activityData.objectives),
        videoPool: mapVideoPool(activityData.videoPool),
        ...(normalizedDueDate ? { dueDate: normalizedDueDate } : {}),
      } as CreateIleInput;

      if (activityData.ileType || initialValues?.ileType) {
        ileInput.ileType = activityData.ileType || initialValues?.ileType;
      }

      const response = await createIleMutation({
        variables: {
          ile: ileInput,
        },
      });

      const createResponse = response.data?.createILE;
      if (!createResponse?.success) {
        throw new Error(createResponse?.message || "Failed to create interactive learning experience");
      }

      const createdIle = createResponse.data?.[0];
      if (!createdIle?.id) {
        throw new Error("Interactive learning experience was created without an ID");
      }

      onSuccess(createdIle.id, createdIle.title, normalizedDueDate);
    } catch (error) {
      onError(getErrorFromUnknown(error));
    }
  };

  return (
    <InteractiveLearningFormDataContainer
      activityData={initialValues}
      onSave={handleSave}
      onCancel={handleCancel}
      isSubmitting={isSubmitting}
      currentDomain={currentDomain}
      imageGenerationSupportFlag={imageGenerationSupportFlag}
      isFromLTI={true}
    />
  );
};

export default InteractiveLearningLTIFormContainer;
